'use client';

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  getAdminSubscriptionActiveCounts,
  getAdminSubscriptionChurn,
  getAdminSubscriptionDetail,
  listAdminSubscriptions,
  listAdminWebhookLogs,
  reprocessAdminWebhookLog,
  syncAdminSubscription,
  type AdminSubscriptionsListParams,
  type AdminWebhookLogsParams,
} from '@nomiss/services';

export const adminSubscriptionsQueryKeys = {
  all: ['admin', 'subscriptions'] as const,
  list: (filters: AdminSubscriptionsListParams) =>
    [...adminSubscriptionsQueryKeys.all, 'list', filters] as const,
  activeCounts: () =>
    [...adminSubscriptionsQueryKeys.all, 'metrics', 'active-counts'] as const,
  churn: (windowDays?: number) =>
    [...adminSubscriptionsQueryKeys.all, 'metrics', 'churn', windowDays] as const,
  detail: (userId: string) =>
    [...adminSubscriptionsQueryKeys.all, 'detail', userId] as const,
  webhookLogs: (filters: AdminWebhookLogsParams) =>
    [...adminSubscriptionsQueryKeys.all, 'webhook-logs', filters] as const,
};

export function useAdminSubscriptionsList(filters: AdminSubscriptionsListParams) {
  const adminSubscriptionsListQuery = useQuery({
    queryKey: adminSubscriptionsQueryKeys.list(filters),
    queryFn: () => listAdminSubscriptions(filters),
  });

  return { adminSubscriptionsListQuery };
}

export function useAdminSubscriptionActiveCounts() {
  const adminSubscriptionActiveCountsQuery = useQuery({
    queryKey: adminSubscriptionsQueryKeys.activeCounts(),
    queryFn: () => getAdminSubscriptionActiveCounts(),
  });

  return { adminSubscriptionActiveCountsQuery };
}

export function useAdminSubscriptionChurn(windowDays = 30) {
  const adminSubscriptionChurnQuery = useQuery({
    queryKey: adminSubscriptionsQueryKeys.churn(windowDays),
    queryFn: () => getAdminSubscriptionChurn(windowDays),
  });

  return { adminSubscriptionChurnQuery };
}

export function useAdminSubscriptionDetail(userId: string | undefined) {
  const adminSubscriptionDetailQuery = useQuery({
    queryKey: adminSubscriptionsQueryKeys.detail(userId ?? ''),
    queryFn: () => getAdminSubscriptionDetail(userId!),
    enabled: Boolean(userId),
  });

  return { adminSubscriptionDetailQuery };
}

export function useAdminWebhookLogs(filters: AdminWebhookLogsParams) {
  const adminWebhookLogsQuery = useQuery({
    queryKey: adminSubscriptionsQueryKeys.webhookLogs(filters),
    queryFn: () => listAdminWebhookLogs(filters),
  });

  return { adminWebhookLogsQuery };
}

export function useSyncAdminSubscription() {
  const queryClient = useQueryClient();

  const syncAdminSubscriptionMutation = useMutation({
    mutationFn: (userId: string) => syncAdminSubscription(userId),
    onSuccess: (_data, userId) => {
      void queryClient.invalidateQueries({
        queryKey: adminSubscriptionsQueryKeys.all,
      });
      void queryClient.invalidateQueries({
        queryKey: adminSubscriptionsQueryKeys.detail(userId),
      });
      void queryClient.invalidateQueries({
        queryKey: ['admin', 'users', 'detail', userId],
      });
    },
  });

  return { syncAdminSubscriptionMutation };
}

export function useReprocessAdminWebhookLog() {
  const queryClient = useQueryClient();

  const reprocessAdminWebhookLogMutation = useMutation({
    mutationFn: (id: string) => reprocessAdminWebhookLog(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: adminSubscriptionsQueryKeys.all,
      });
    },
  });

  return { reprocessAdminWebhookLogMutation };
}
